import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { checkPostcode } from "@/lib/api";
import { Reveal, SectionHead } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const REGIONS = ["Inner Melbourne & CBD", "Bayside & Brighton", "Eastern suburbs", "Northern suburbs", "Western growth corridor", "South-East & Casey", "Mornington Peninsula", "Sunbury & Macedon Ranges"];

export const ServiceArea = () => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setResult(await checkPostcode(code)); } catch { setResult({ valid: false, message: "Could not check right now. Please try again." }); }
    finally { setBusy(false); }
  };

  return (
    <section id="service-area" className="py-24 lg:py-36 bg-linen-2 border-t border-line" data-testid="service-area-section">
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <div>
          <SectionHead eyebrow="Service Area" title="Melbourne metro and the suburbs around it." desc="Our installers cover greater Melbourne — from the CBD to the Peninsula. Enter your postcode to confirm a free in-home measure is available at your address." />
          <Reveal delay={0.2}>
            <ul className="mt-10 grid grid-cols-2 gap-y-3 gap-x-6" data-testid="service-regions">
              {REGIONS.map((r) => <li key={r} className="flex items-center gap-2 text-sm text-ink-2"><MapPin size={14} className="text-brass shrink-0" />{r}</li>)}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="bg-white border border-line p-8 lg:p-10">
            <p className="eyebrow mb-4">Postcode check</p>
            <h3 className="font-serif text-3xl text-ink">Do we come to you?</h3>
            <form onSubmit={check} className="mt-6 flex gap-3">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="e.g. 3142" className="field font-mono text-lg tracking-widest" aria-label="Postcode" data-testid="postcode-input" />
              <button type="submit" disabled={busy || code.length !== 4} className="btn-ink shrink-0 disabled:opacity-40 disabled:hover:translate-y-0" data-testid="postcode-check-btn">{busy ? "Checking…" : "Check"}</button>
            </form>
            <AnimatePresence mode="wait">
              {result && (
                <motion.div key={result.postcode + String(result.serviced)} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className={`mt-6 p-5 border flex gap-3 ${result.serviced ? "border-brass/40 bg-brass/5" : "border-line bg-linen-2"}`} data-testid="postcode-result">
                  {result.serviced ? <CheckCircle2 className="text-brass shrink-0" size={20} /> : <AlertCircle className="text-stone shrink-0" size={20} />}
                  <div>
                    {result.region && <p className="eyebrow mb-1" data-testid="postcode-region">{result.region}</p>}
                    <p className="text-sm text-ink-2 leading-relaxed" data-testid="postcode-message">{result.message}</p>
                    {result.serviced && <button type="button" onClick={() => scrollToId("book")} className="mt-4 text-xs font-semibold tracking-wide text-brass hover:text-brass-hover underline underline-offset-4" data-testid="postcode-book-btn">Book your free measure →</button>}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
